/**
 * Floor Plan Canlı Görünüm — masaların gerçek zamanlı durumu.
 *
 * Rezervasyon overlay: rezerve masalarda saat badge'i.
 * Süre göstergeleri: dolu masada "kaç dk açık" + "son sipariş X dk önce".
 * WS subscription: table.state_changed real-time.
 */
import {
  Alert, Badge, Box, Button, Card, Divider, Drawer, Grid, Group, Loader, Modal,
  Paper, Select, SimpleGrid, Stack, Text, Textarea, Title,
} from "@mantine/core";
import {
  IconClock, IconToolsKitchen2, IconUser, IconReceipt2, IconRefresh, IconCash,
  IconBell, IconSparkles, IconPlus, IconCheck, IconX, IconArrowsExchange,
  IconCalendarTime,
} from "@tabler/icons-react";
import { NewOrderDrawer } from "./Orders";
import { notifications } from "@mantine/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  useFloorPlans, useReservations, useTables, useTableDetail,
  useTableStatusOverview, useTransitionTable, useUpdateTable, useWaiterOptions,
  useAction, useCancelOrder, useTenderSession,
} from "../../../shared/api";
import { useWebSocket } from "../../../shared/ws";
import { MoneyDisplay, PageHeader, PaymentSheet, StatusBadge, TransferLinesModal } from "../../../shared/ui";
import { LineCompExchangeActions } from "../components/LineCompExchangeActions";

const STATE_COLOR_BG: Record<string, string> = {
  FREE: "#d3f9d8",
  RESERVED: "#fff3bf",
  OCCUPIED: "#ffc9c9",
  CLEANING: "#e9ecef",
  OUT_OF_SERVICE: "#495057",
};

// QR'dan hesap isteyen masa: state ne olursa olsun mor zeminle öne çıkar
// (POS Masa Planı ile aynı görsel dil).
const PAYMENT_REQUESTED_BG = "#f3d9fa";

/**
 * "Yeni QR sipariş onayı" rozeti için yanıp sönme animasyonu — POS Masa
 * Planı ile birebir aynı; garson/yönetici onay bekleyeni kaçırmasın.
 */
const BLINK_KEYFRAMES = `
@keyframes floorplan-blink {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%      { opacity: 0.35; transform: scale(0.88); }
}
`;

/** Bir zaman damgasından bu ana kadar geçen süre — kısa biçim ("12 dk"). */
function elapsedShort(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return "az önce";
  const min = Math.floor(ms / 60000);
  if (min < 1) return "az önce";
  if (min < 60) return `${min} dk`;
  const h = Math.floor(min / 60);
  return `${h} sa ${min % 60} dk`;
}

/** Geçen süreye göre renk — uzun bekleyen masa dikkat çeksin. */
function elapsedColor(iso: string | null | undefined): string {
  if (!iso) return "gray";
  const min = (Date.now() - new Date(iso).getTime()) / 60000;
  if (min >= 45) return "red";
  if (min >= 20) return "orange";
  return "gray";
}

export function FloorPlanLivePage() {
  const qc = useQueryClient();

  // WS bağlıyken polling kapalı, koparsa fallback (useTables/Overview içinde).
  const { lastMessage: wsMessage, status: wsStatus } =
    useWebSocket<any>("/ws/waiters/");
  const wsOk = wsStatus === "OPEN";

  const { data: floorPlans } = useFloorPlans();
  const { data: tables, isLoading: tablesLoading } = useTables({ wsConnected: wsOk });
  const { data: reservations } = useReservations({ status: "CONFIRMED" });
  const { data: overview } = useTableStatusOverview({ wsConnected: wsOk });
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  // Masaya tıklanınca açılan detay Drawer'ı
  const [detailTableId, setDetailTableId] = useState<string | null>(null);

  // Süre etiketleri canlı aksın diye 30 sn'de bir yeniden render
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  // Masa durumu / sipariş push'ları — anlık tazele.
  useEffect(() => {
    if (!wsMessage) return;
    const ev = wsMessage.event;
    if (
      ev === "table.state_changed" ||
      ev === "order.confirmed" ||
      ev === "payment.completed" ||
      ev === "qr.order.pending_approval" ||
      ev === "qr.payment.requested" ||
      ev === "qr.payment.call_waiter" ||
      ev === "order.lines_transferred"
    ) {
      qc.invalidateQueries({ queryKey: ["tables"] });
      qc.invalidateQueries({ queryKey: ["table-overview"] });
      qc.invalidateQueries({ queryKey: ["table-detail"] });
    }
    // Hesap aktarımı — admin de bildirim alsın (kimin hangi masayı
    // boşalttığı görsel olarak takip edilebilsin).
    if (ev === "order.lines_transferred") {
      const sourceName = wsMessage.source_table_name || "—";
      const targetName = wsMessage.target_table_name || "—";
      const count = wsMessage.line_count || 0;
      notifications.show({
        title: "🔀 Hesap Aktarıldı",
        message: `${sourceName} → ${targetName}: ${count} satır`,
        color: "blue",
        autoClose: 6000,
      });
    }
  }, [wsMessage, qc]);

  const plans = floorPlans?.results || floorPlans || [];
  const allTables = tables?.results || tables || [];
  const tablesInPlan = selectedPlanId
    ? allTables.filter((t: any) => t.floor_plan === selectedPlanId)
    : allTables;

  // Masa → bugünkü en yakın rezervasyon eşle.
  // Salon planı CANLI operasyon ekranı: yalnızca BUGÜN başlayacak
  // rezervasyonlar "rezervasyonlu masa" rozeti basmalı. İleri tarihli
  // (örn. 7 gün sonraki) rezervasyon burada gösterilmemeli — aksi halde
  // bugün dolu olan masa, günler sonrasının rezervasyonu yüzünden yanlış
  // biçimde "rezervasyonlu" görünür. Aynı masaya birden çok bugünkü
  // rezervasyon varsa en erken başlayan seçilir.
  const reservMap = new Map<string, any>();
  const now = new Date();
  const todayStr = now.toDateString();
  ((reservations?.results || reservations || []) as any[])
    .filter((r) => {
      if (!r.starts_at) return false;
      const start = new Date(r.starts_at);
      // Bugün içinde ve henüz geçmemiş (başlangıcı şimdiden önce değil).
      return start.toDateString() === todayStr && start >= now;
    })
    .sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime())
    .forEach((r) => {
      if (!reservMap.has(r.table)) reservMap.set(r.table, r);
    });

  // POS Masa Planı ile aynı sayaçlar: QR'dan hesap isteyen masa adedi +
  // garson onayı bekleyen toplam yeni QR sipariş adedi.
  const paymentRequestedCount = tablesInPlan.filter(
    (t: any) => t.payment_requested,
  ).length;
  const newOrderCount = tablesInPlan.reduce(
    (sum: number, t: any) => sum + (t.qr_pending_count || 0),
    0,
  );

  return (
    <Stack gap="md">
      <style>{BLINK_KEYFRAMES}</style>
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <PageHeader
          title="Salon Planı (Canlı)"
          subtitle="Masaların gerçek zamanlı durumu — rezervasyonlar görünür"
        />
        <Stack gap={6} align="flex-end">
          {paymentRequestedCount > 0 && (
            <Badge
              size="lg"
              variant="filled"
              color="grape"
              leftSection={<IconCash size={14} />}
            >
              {paymentRequestedCount} masa hesap istiyor
            </Badge>
          )}
          {newOrderCount > 0 && (
            <Badge
              size="lg"
              variant="filled"
              color="orange"
              leftSection={<IconBell size={14} />}
              style={{ animation: "floorplan-blink 1.2s ease-in-out infinite" }}
            >
              {newOrderCount} yeni sipariş onay
            </Badge>
          )}
        </Stack>
      </Group>

      {/* Üst özet — mobilde kompakt: 3'lü/5'li ızgara, küçük kartlar */}
      {overview && (
        <SimpleGrid cols={{ base: 2, xs: 3, sm: 5 }} spacing="xs">
          {Object.entries(overview).map(([state, count]) => (
            <Card key={state} padding={6} radius="md" withBorder>
              <Group gap={6} wrap="nowrap" justify="center">
                <StatusBadge status={state} />
                <Text fw={700} size="sm">{count as number}</Text>
              </Group>
            </Card>
          ))}
        </SimpleGrid>
      )}

      {plans.length > 1 && (
        <Select
          placeholder="Salon seçin"
          data={plans.map((p: any) => ({ value: p.id, label: p.name }))}
          value={selectedPlanId}
          onChange={setSelectedPlanId}
          clearable
        />
      )}

      {tablesLoading ? <Loader /> : (
        <Paper p="lg" radius="md" withBorder>
          <Grid gutter="md" align="stretch">
            {tablesInPlan.map((t: any) => {
              const reservation = reservMap.get(t.id);
              const isOccupied = t.state === "OCCUPIED";
              // QR'dan hesap istendiyse masa mor zeminle öne çıkar (state'i ezer).
              const paymentRequested = !!t.payment_requested;
              // Garson onayı bekleyen QR sipariş sayısı (DRAFT)
              const qrPending = t.qr_pending_count || 0;
              const bg = paymentRequested
                ? PAYMENT_REQUESTED_BG
                : STATE_COLOR_BG[t.state] || "#fff";
              // Açık süresi: OCCUPIED'a geçtiğinden beri (state_changed_at)
              const openFor = isOccupied ? elapsedShort(t.state_changed_at) : null;
              // Son siparişten geçen süre
              const sinceOrder = isOccupied ? elapsedShort(t.last_order_at) : null;
              return (
                <Grid.Col key={t.id} span={{ base: 6, sm: 4, md: 3, lg: 2 }}>
                  <Card
                    padding="sm" radius="md" withBorder
                    onClick={() => setDetailTableId(t.id)}
                    style={{
                      background: bg,
                      cursor: "pointer",
                      // Aynı satırdaki kartlar eşit yükseklikte olsun: kart
                      // Grid.Col'u (stretch) doldurur, minHeight taban verir.
                      height: "100%",
                      minHeight: 150,
                      // Hesap isteyen masa mor çerçeveyle de vurgulanır.
                      ...(paymentRequested && {
                        borderColor: "var(--mantine-color-grape-6)",
                        borderWidth: 2,
                      }),
                      // 3 bölgeli dikey yapı: üst rozetler / orta (masa adı) /
                      // alt rozetler. Absolute overlay yerine normal akış →
                      // mobilde dar kartta çakışma olmaz.
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {/* Üst şerit — sol: sipariş/yeni, sağ: rezervasyon/süre.
                        Sol grup küçülebilir (minWidth:0) → sağdaki süre sabit
                        kalır, aşağı düşmez. */}
                    <Group justify="space-between" align="flex-start" wrap="nowrap" gap={4} mih={22}>
                      <Group gap={4} wrap="wrap" style={{ minWidth: 0 }}>
                        {t.open_order_count > 0 && (
                          <Badge size="sm" variant="filled" color="blue">
                            {t.open_order_count} sipariş
                          </Badge>
                        )}
                        {qrPending > 0 && (
                          <Badge
                            size="sm"
                            variant="filled"
                            color="orange"
                            leftSection={<IconBell size={10} />}
                            style={{ animation: "floorplan-blink 1.2s ease-in-out infinite" }}
                            title={`${qrPending} QR siparişi onay bekliyor`}
                          >
                            {qrPending} yeni
                          </Badge>
                        )}
                      </Group>
                      {reservation ? (
                        <Badge color="yellow" variant="filled" size="sm" style={{ flexShrink: 0 }}>
                          📅 {new Date(reservation.starts_at).toLocaleTimeString("tr", {
                            hour: "2-digit", minute: "2-digit",
                          })}
                        </Badge>
                      ) : openFor ? (
                        <Group gap={3} wrap="nowrap" style={{ flexShrink: 0 }}>
                          <IconClock
                            size={12}
                            color={`var(--mantine-color-${elapsedColor(t.state_changed_at)}-7)`}
                          />
                          <Text size="10px" fw={700} c={elapsedColor(t.state_changed_at)} style={{ whiteSpace: "nowrap" }}>
                            {openFor}
                          </Text>
                        </Group>
                      ) : null}
                    </Group>

                    {/* Orta — masa adı + durum (esner, dikey ortalanır) */}
                    <Stack gap={6} align="center" justify="center" style={{ flex: 1 }} py={4}>
                      <Title order={3} ta="center" lineClamp={1}>{t.name}</Title>
                      <StatusBadge status={t.state} />
                      {paymentRequested && (
                        <Badge
                          color="grape"
                          size="sm"
                          variant="filled"
                          leftSection={<IconCash size={12} />}
                        >
                          Hesap İstiyor
                        </Badge>
                      )}
                      <Text size="xs" c="dimmed">{t.capacity} kişi</Text>
                      {reservation && (
                        <Text size="xs" ta="center" lineClamp={1}>
                          {reservation.customer_name || reservation.customer_phone || "Misafir"} ({reservation.party_size})
                        </Text>
                      )}
                    </Stack>

                    {/* Alt şerit — garson + son sipariş süresi. Garson adı
                        ASLA kısaltılmaz (ellipsis kapalı); sığmazsa kendi
                        satırına sarar. */}
                    {(t.assigned_waiter_name || sinceOrder) && (
                      <Stack gap={2} mt={4}>
                        {t.assigned_waiter_name && (
                          <Badge
                            size="xs"
                            variant="filled"
                            color="grape"
                            leftSection={<IconUser size={10} />}
                            styles={{
                              root: { maxWidth: "unset", height: "auto" },
                              label: { overflow: "visible", whiteSpace: "normal" },
                            }}
                          >
                            {t.assigned_waiter_name}
                          </Badge>
                        )}
                        {sinceOrder && (
                          <Group gap={3} wrap="nowrap">
                            <IconToolsKitchen2 size={11} color="var(--mantine-color-gray-7)" />
                            <Text size="9px" c="dimmed">
                              son sipariş {sinceOrder} önce
                            </Text>
                          </Group>
                        )}
                      </Stack>
                    )}
                  </Card>
                </Grid.Col>
              );
            })}
          </Grid>
          {tablesInPlan.length === 0 && (
            <Text c="dimmed" ta="center" py="xl">Bu salon için masa yok</Text>
          )}
        </Paper>
      )}

      <TableDetailDrawer
        tableId={detailTableId}
        reservation={detailTableId ? reservMap.get(detailTableId) : null}
        onClose={() => setDetailTableId(null)}
      />
    </Stack>
  );
}

// ===========================================================================
// Masa detay Drawer'ı — güncel masa görünümü (geçmiş raporlarda)
// ===========================================================================
function TableDetailDrawer({
  tableId, reservation, onClose,
}: {
  tableId: string | null;
  reservation?: any;
  onClose: () => void;
}) {
  const { data, isLoading } = useTableDetail(tableId);
  const qc = useQueryClient();
  const transitionTable = useTransitionTable();
  const confirmOrder = useAction("orders", "confirm");
  const cancelOrder = useCancelOrder();
  const tenderSession = useTenderSession();
  const [payOpen, setPayOpen] = useState(false);
  const [newOrderOpen, setNewOrderOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  // İptal modal — prompt() yerine düzgün Mantine modal'la sebep al.
  const [cancelTargetId, setCancelTargetId] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  // useAction/useCancelOrder mutation'ları paylaşılan (isPending tek). O yüzden
  // hangi sipariş üzerinde işlem yapıldığını ayrıca tutuyoruz — her satır
  // sadece kendi butonunda spinner göstersin.
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  function refetchTable() {
    qc.invalidateQueries({ queryKey: ["table-detail"] });
    qc.invalidateQueries({ queryKey: ["tables"] });
    qc.invalidateQueries({ queryKey: ["table-overview"] });
    qc.invalidateQueries({ queryKey: ["orders"] });
  }

  async function handleConfirmOrder(orderId: string) {
    setConfirmingId(orderId);
    try {
      await confirmOrder.mutateAsync({ id: orderId });
      notifications.show({ message: "Sipariş onaylandı — mutfağa gönderildi", color: "green" });
      refetchTable();
    } catch (e: any) {
      notifications.show({
        message: e?.response?.data?.detail || "Sipariş onaylanamadı",
        color: "red",
      });
    } finally {
      setConfirmingId(null);
    }
  }

  function openCancelModal(orderId: string) {
    setCancelTargetId(orderId);
    setCancelReason("");
  }

  async function submitCancel() {
    if (!cancelTargetId) return;
    try {
      await cancelOrder.mutateAsync({
        orderId: cancelTargetId,
        reason: cancelReason.trim(),
      });
      notifications.show({ message: "Sipariş iptal edildi", color: "green" });
      setCancelTargetId(null);
      setCancelReason("");
      refetchTable();
    } catch (e: any) {
      notifications.show({
        message: e?.response?.data?.detail || "Sipariş iptal edilemedi",
        color: "red",
      });
    }
  }

  const orders: any[] = data?.orders || [];
  // QR'dan hesap isteği özeti (split mode / parça / kişi başı tutar)
  const paymentRequest = data?.payment_request || null;
  // Garson onayı bekleyen QR siparişleri (DRAFT) — masa detayında uyar
  const qrPendingOrders = orders.filter(
    (o: any) => o.source === "QR" && o.kitchen_status === "DRAFT",
  );
  // Sadece onaylanmış (ödenebilir) siparişler — DRAFT/CANCELLED dahil değil.
  // Backend table-detail endpoint'i toplam_minor'a DRAFT'ları da katıyor
  // ("Hesap Al" yanlış tutar gösteriyordu). Frontend'de yeniden hesapla.
  const payableOrders = orders.filter(
    (o: any) => !["DRAFT", "CANCELLED", "CLOSED"].includes(o.kitchen_status),
  );
  const payableTotalMinor: number = payableOrders.reduce(
    (s: number, o: any) => s + (o.total_minor || 0),
    0,
  );
  const draftCount = orders.filter((o: any) => o.kitchen_status === "DRAFT").length;

  return (
    <Drawer
      opened={!!tableId}
      onClose={onClose}
      position="right"
      size="md"
      title={data?.table?.name ? `🍽 ${data.table.name}` : "Masa Detayı"}
    >
      {isLoading || !data ? (
        <Loader />
      ) : (
        <Stack gap="md">
          {/* QR'dan hesap isteği — masaya tıklanınca yönetici hemen görsün */}
          {paymentRequest && (
            <Alert
              color="grape"
              icon={<IconCash size={18} />}
              title="Bu masa hesap istiyor"
            >
              {paymentRequest.split_mode === "split"
                ? `${paymentRequest.split_parts} parça halinde ödeme — kişi başı `
                : "Tek ödeme — "}
              <MoneyDisplay
                minor={
                  paymentRequest.split_mode === "split"
                    ? paymentRequest.per_part_minor
                    : paymentRequest.total_minor
                }
                fw={700}
                size="sm"
              />
            </Alert>
          )}

          {/* Garson onayı bekleyen QR siparişleri */}
          {qrPendingOrders.length > 0 && (
            <Alert
              color="orange"
              icon={<IconBell size={18} />}
              title={`${qrPendingOrders.length} QR siparişi onay bekliyor`}
            >
              Müşteri QR'dan sipariş verdi — garson onayı bekleniyor.
            </Alert>
          )}

          {/* Rezervasyon — bu masa rezervasyondan geldi/gelecek. Yönetici
              masaya tıklayınca rezervasyon saatini + misafiri görür. */}
          {reservation && (
            <Alert
              color="yellow"
              icon={<IconCalendarTime size={18} />}
              title="Rezervasyonlu masa"
            >
              {new Date(reservation.starts_at).toLocaleString("tr-TR", {
                day: "2-digit", month: "2-digit",
                hour: "2-digit", minute: "2-digit",
              })}
              {" · "}
              {reservation.customer_name || reservation.customer_phone || "Misafir"}
              {" · "}{reservation.party_size} kişi
              {reservation.notes ? ` · ${reservation.notes}` : ""}
            </Alert>
          )}

          {/* Üst özet kartı */}
          <Card padding="md" radius="md" withBorder>
            <Group justify="space-between" mb="xs">
              <StatusBadge status={data.table.state} />
              <Text size="sm" c="dimmed">{data.table.capacity} kişilik</Text>
            </Group>
            <Group gap="lg">
              <Box>
                <Group gap={4}>
                  <IconClock size={14} color="var(--mantine-color-gray-6)" />
                  <Text size="xs" c="dimmed">Açık süre</Text>
                </Group>
                <Text fw={700}>
                  {elapsedShort(data.table.state_changed_at) || "—"}
                </Text>
              </Box>
              <Box>
                <Group gap={4}>
                  <IconToolsKitchen2 size={14} color="var(--mantine-color-gray-6)" />
                  <Text size="xs" c="dimmed">Son sipariş</Text>
                </Group>
                <Text fw={700}>
                  {data.table.last_order_at
                    ? `${elapsedShort(data.table.last_order_at)} önce`
                    : "—"}
                </Text>
              </Box>
              <Box>
                <Group gap={4}>
                  <IconRefresh size={14} color="var(--mantine-color-gray-6)" />
                  <Text size="xs" c="dimmed">Bugün açılma</Text>
                </Group>
                <Text fw={700}>{data.opened_today} kez</Text>
              </Box>
            </Group>
          </Card>

          {/* Temizlik bitti — masa CLEANING'de takılıysa manuel FREE'ye çek.
              (Otomatik geçiş için: Ayarlar > table_cleaning_auto_free_minutes;
              0 değilse Celery beat zaten süre dolunca boşaltır.) */}
          {data.table.state === "CLEANING" && (
            <Button
              fullWidth
              size="md"
              color="green"
              variant="light"
              leftSection={<IconSparkles size={18} />}
              loading={transitionTable.isPending}
              onClick={() =>
                transitionTable.mutate(
                  { tableId: data.table.id, state: "FREE" },
                  {
                    onSuccess: () => {
                      notifications.show({
                        message: "Masa temizlendi — boşa alındı",
                        color: "green",
                      });
                      onClose();
                    },
                    onError: (e: any) =>
                      notifications.show({
                        message:
                          e?.response?.data?.detail || "İşlem başarısız",
                        color: "red",
                      }),
                  },
                )
              }
            >
              Temizlik Bitti — Masayı Boşa Al
            </Button>
          )}

          {/* İlgili garson ataması — masaya sabit garson seç */}
          <AssignWaiterSelect
            tableId={data.table.id}
            currentWaiterId={data.table.assigned_waiter || null}
          />

          {/* İlgilenen garson(lar) — açık siparişlere bağlı (canlı) */}
          {data.waiters.length > 0 && (
            <Group gap="xs">
              <IconUser size={16} color="var(--mantine-color-gray-6)" />
              <Text size="sm" c="dimmed">İlgilenen:</Text>
              {data.waiters.map((w: any) => (
                <Badge key={w.id} variant="light" color="grape">
                  {w.name}
                </Badge>
              ))}
            </Group>
          )}

          {/* Adisyon (TableSession) üst başlığı — GİB Adisyon Tebliği:
              1 masa = 1 adisyon. Kısa ID + açılış saati + LOCKED ise rozet. */}
          {data?.session && (
            <Group gap={6} wrap="nowrap">
              <Badge size="md" variant="filled" color="indigo">
                Adisyon #{data.session.short_id}
              </Badge>
              <Text size="xs" c="dimmed">
                · açıldı{" "}
                {new Date(data.session.opened_at).toLocaleTimeString("tr-TR", {
                  hour: "2-digit", minute: "2-digit",
                })}
              </Text>
              {data.session.status === "LOCKED" && (
                <Badge size="xs" variant="light" color="yellow">
                  Hesap istendi
                </Badge>
              )}
            </Group>
          )}

          {/* Toplam — yalnız ödenebilir (onaylanmış) siparişler */}
          <Stack gap={4}>
            <Group justify="space-between">
              <Group gap={6}>
                <IconReceipt2 size={18} />
                <Text fw={600}>
                  {payableOrders.length} ödenebilir sipariş
                </Text>
              </Group>
              <MoneyDisplay minor={payableTotalMinor} fw={800} size="lg" c="blue" />
            </Group>
            {draftCount > 0 && (
              <Text size="xs" c="dimmed">
                {draftCount} taslak sipariş hesaba dahil değil (önce onaylayın
                veya iptal edin).
              </Text>
            )}
          </Stack>

          {/* Yeni Sipariş — DINE_IN + bu masa pre-fill */}
          {data.table.state !== "OUT_OF_SERVICE" && (
            <Button
              size="lg"
              fullWidth
              variant="filled"
              color="blue"
              leftSection={<IconPlus size={20} />}
              onClick={() => setNewOrderOpen(true)}
            >
              Yeni Sipariş Ekle
            </Button>
          )}

          {/* Hesap Al — ödenebilir siparişler için. Tutar > 0 ise PaymentSheet
              açılır; tutar 0 ise (tamamı ikram/iade) ödeme alınamaz —
              doğrudan adisyonu boş tender ile kapat, masa boşalsın. */}
          {payableOrders.length > 0 && (
            <Button
              size="lg"
              fullWidth
              leftSection={<IconCash size={20} />}
              loading={tenderSession.isPending}
              onClick={() => {
                if (payableTotalMinor > 0) {
                  setPayOpen(true);
                  return;
                }
                if (!data?.session?.id) return;
                tenderSession.mutate(
                  { sessionId: data.session.id, tenders: [] },
                  {
                    onSuccess: () => {
                      notifications.show({
                        message: "Adisyon kapatıldı — ödenecek tutar yoktu (ikram/iade)",
                        color: "green",
                      });
                      qc.invalidateQueries({ queryKey: ["table-detail"] });
                      qc.invalidateQueries({ queryKey: ["tables"] });
                      qc.invalidateQueries({ queryKey: ["table-overview"] });
                      onClose();
                    },
                    onError: (e: any) =>
                      notifications.show({
                        message: e?.response?.data?.detail || "Kapatılamadı",
                        color: "red",
                      }),
                  },
                );
              }}
            >
              {payableTotalMinor > 0 ? (
                <>Hesap Al · <MoneyDisplay minor={payableTotalMinor} fw={700} /></>
              ) : (
                "Adisyonu Kapat (ikram — ₺0)"
              )}
            </Button>
          )}

          {/* Hesap Aktar — ADİSYON seviyesinde tek buton. Modal tüm açık
              siparişlerin kalemlerini gösterir; seçilenler hedef masada tek
              hesapta birleşir (10 sipariş × 50 kalemden ilgilileri seç). */}
          {data.orders.some(
            (o: any) => !["CLOSED", "CANCELLED"].includes(o.kitchen_status),
          ) && (
            <Button
              size="md"
              fullWidth
              variant="light"
              color="blue"
              leftSection={<IconArrowsExchange size={18} />}
              onClick={() => setTransferOpen(true)}
            >
              Hesap Aktar (Transfer)
            </Button>
          )}

          <Divider />

          {/* Açık siparişler — satırlarıyla */}
          {data.orders.length === 0 ? (
            <Text c="dimmed" ta="center" py="md">
              Bu masada açık sipariş yok
            </Text>
          ) : (
            data.orders.map((o: any, idx: number) => (
              <Card key={o.id} padding="sm" radius="md" withBorder>
                <Group justify="space-between" mb={6}>
                  <Group gap="xs">
                    <Badge size="sm" variant="filled" color="gray">
                      #{idx + 1}
                    </Badge>
                    {/* Sipariş No — Orders listesiyle aynı UUID prefix biçimi */}
                    <Text size="xs" c="dimmed" ff="monospace">
                      {String(o.id).slice(0, 8)}
                    </Text>
                    <StatusBadge status={o.kitchen_status} />
                    {o.source === "QR" && (
                      <Badge size="xs" variant="light" color="violet">QR</Badge>
                    )}
                    {/* Hesap aktarımı izi — başka masadan gelen sipariş.
                        Çok adımlı transferlerde tüm zincir (M3 → M4 → M5);
                        tek adımda sadece kaynak masa. */}
                    {(o.transfer_chain && o.transfer_chain.length > 0)
                      ? (
                        <Badge size="xs" variant="light" color="blue">
                          ↪ {o.transfer_chain.join(" → ")}
                        </Badge>
                      )
                      : o.transferred_from_table_name && (
                        <Badge size="xs" variant="light" color="blue">
                          ↪ {o.transferred_from_table_name}'tan
                        </Badge>
                      )}
                  </Group>
                  <MoneyDisplay minor={o.total_minor} fw={700} />
                </Group>
                {o.waiter_name && (
                  <Text size="xs" c="dimmed" mb={4}>
                    Garson: {o.waiter_name} ·{" "}
                    {new Date(o.created_at).toLocaleTimeString("tr-TR", {
                      hour: "2-digit", minute: "2-digit",
                    })}
                  </Text>
                )}
                <Stack gap={2}>
                  {o.lines.map((ln: any) => (
                    <Group key={ln.id} justify="space-between" wrap="nowrap">
                      <Group gap={6} wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                        <Text size="sm" lineClamp={1}>
                          {ln.quantity}× {ln.product_name}
                        </Text>
                        {ln.comp_quantity_approved > 0 && (
                          <Badge size="xs" color="pink" variant="light" style={{ flexShrink: 0 }}>
                            🎁 {ln.comp_quantity_approved} ikram
                          </Badge>
                        )}
                        {ln.returned_quantity_approved > 0 && (
                          <Badge size="xs" color="indigo" variant="light" style={{ flexShrink: 0 }}>
                            ↩ {ln.returned_quantity_approved} iade
                          </Badge>
                        )}
                      </Group>
                      <Group gap={4} wrap="nowrap">
                        <MoneyDisplay minor={ln.line_total_minor} size="sm" c="dimmed" />
                        {ln.id && (
                          <LineCompExchangeActions
                            orderId={o.id}
                            line={ln}
                            kitchenStatus={o.kitchen_status}
                          />
                        )}
                      </Group>
                    </Group>
                  ))}
                </Stack>

                {/* Hesap Aktar artık adisyon seviyesinde tek buton (yukarıda) —
                    per-order buton kaldırıldı; 50 kalemden ilgilileri tek
                    modalda seçilir. */}

                {/* DRAFT siparişler için onayla / iptal — super admin & owner
                    QR ya da kasiyer tarafından açılmış taslakları doğrudan
                    onaylayabilsin diye drawer üzerinden de erişilebilir.
                    Boş DRAFT (ürün satırı yok) onaylanamaz — backend reddediyor
                    ("Boş sipariş onaylanamaz"); Onayla butonu hiç gösterilmez,
                    sadece iptal sunulur. */}
                {o.kitchen_status === "DRAFT" && (() => {
                  const hasLines = (o.lines || []).length > 0;
                  return (
                    <Stack gap="xs" mt="sm">
                      {!hasLines && (
                        <Text size="xs" c="dimmed" fs="italic">
                          Ürün satırı yok — onaylanamaz, sadece iptal edilebilir.
                        </Text>
                      )}
                      <Group gap="xs" grow>
                        {hasLines && (
                          <Button
                            size="xs"
                            color="green"
                            leftSection={<IconCheck size={14} />}
                            loading={confirmingId === o.id}
                            disabled={confirmingId !== null && confirmingId !== o.id}
                            onClick={() => handleConfirmOrder(o.id)}
                          >
                            Onayla
                          </Button>
                        )}
                        <Button
                          size="xs"
                          color="red"
                          variant="light"
                          leftSection={<IconX size={14} />}
                          loading={cancelOrder.isPending && cancelTargetId === o.id}
                          disabled={confirmingId !== null && confirmingId !== o.id}
                          onClick={() => openCancelModal(o.id)}
                        >
                          İptal Et
                        </Button>
                      </Group>
                    </Stack>
                  );
                })()}
              </Card>
            ))
          )}

          {/* Hesap Aktar — adisyon seviyesinde, tüm açık siparişlerin
              kalemlerinden seçim. Seçilenler hedef masada tek hesapta birleşir. */}
          <TransferLinesModal
            opened={transferOpen}
            onClose={() => setTransferOpen(false)}
            sourceTableId={tableId}
            orders={(data.orders || []).filter(
              (o: any) => !["CLOSED", "CANCELLED"].includes(o.kitchen_status),
            )}
          />

          {/* Hesap Al modalı — ödeme alınınca masa kapanır */}
          <PaymentSheet
            opened={payOpen}
            onClose={() => setPayOpen(false)}
            sessionId={data?.session?.id || null}
            orders={payableOrders.map((o) => ({
              id: o.id,
              total_minor: o.total_minor,
              source: o.source,
              waiter_name: o.waiter_name,
              lines: o.lines,
            }))}
            onPaid={() => {
              setPayOpen(false);
              notifications.show({
                message: "Ödeme alındı — masa kapatıldı",
                color: "green",
              });
              // Masa detayı + salon planı + özet tazelensin
              qc.invalidateQueries({ queryKey: ["table-detail"] });
              qc.invalidateQueries({ queryKey: ["tables"] });
              qc.invalidateQueries({ queryKey: ["table-overview"] });
              onClose();
            }}
          />

          {/* Yeni Sipariş — masa pre-fill + kilitli (DINE_IN). Atanmış
              garson varsa ön seçili gelir; yoksa kullanıcı seçer. */}
          <NewOrderDrawer
            opened={newOrderOpen}
            onClose={() => {
              setNewOrderOpen(false);
              // Sipariş eklendiyse masa detayı + salon planı tazelensin.
              qc.invalidateQueries({ queryKey: ["table-detail"] });
              qc.invalidateQueries({ queryKey: ["tables"] });
              qc.invalidateQueries({ queryKey: ["table-overview"] });
              qc.invalidateQueries({ queryKey: ["orders"] });
            }}
            initialSource="POS"
            initialFulfillment="DINE_IN"
            initialTableId={data.table.id}
            initialWaiterId={data.table.assigned_waiter || null}
            lockTable
          />

          {/* İptal sebebi modal — prompt() yerine */}
          <Modal
            opened={!!cancelTargetId}
            onClose={() => {
              if (cancelOrder.isPending) return;
              setCancelTargetId(null);
              setCancelReason("");
            }}
            title="Siparişi İptal Et"
            centered
            zIndex={300}
          >
            <Stack gap="md">
              <Text size="sm" c="dimmed">
                Bu sipariş iptal edilecek. İsterseniz sebep yazabilirsiniz
                (opsiyonel, geçmiş raporlarında görünür).
              </Text>
              <Textarea
                label="İptal sebebi"
                placeholder="Örn: müşteri vazgeçti, yanlış masa, ürün stokta yok..."
                value={cancelReason}
                onChange={(e) => setCancelReason(e.currentTarget.value)}
                autosize
                minRows={2}
                maxRows={5}
                data-autofocus
              />
              <Group justify="flex-end" gap="xs">
                <Button
                  variant="default"
                  onClick={() => {
                    setCancelTargetId(null);
                    setCancelReason("");
                  }}
                  disabled={cancelOrder.isPending}
                >
                  Vazgeç
                </Button>
                <Button
                  color="red"
                  leftSection={<IconX size={14} />}
                  loading={cancelOrder.isPending}
                  onClick={submitCancel}
                >
                  Siparişi İptal Et
                </Button>
              </Group>
            </Stack>
          </Modal>
        </Stack>
      )}
    </Drawer>
  );
}

// ===========================================================================
// AssignWaiterSelect — masaya sabit "ilgili garson" ataması
// ===========================================================================
function AssignWaiterSelect({
  tableId, currentWaiterId,
}: {
  tableId: string;
  currentWaiterId: string | null;
}) {
  const { data: waiterOptions } = useWaiterOptions();
  const updateTable = useUpdateTable();

  function handleChange(value: string | null) {
    if (value === currentWaiterId) return;
    updateTable.mutate(
      { tableId, patch: { assigned_waiter: value } },
      {
        onSuccess: () =>
          notifications.show({
            message: value
              ? "Masa garsonu güncellendi"
              : "Masa garson ataması kaldırıldı",
            color: "green",
          }),
        onError: (e: any) =>
          notifications.show({
            message: e?.response?.data?.detail || "Garson ataması başarısız",
            color: "red",
          }),
      },
    );
  }

  return (
    <Select
      label="İlgili garson"
      description="QR onay ekranı bu masayı atanan garsona göre renklendirir"
      placeholder="Garson seçin"
      leftSection={<IconUser size={16} />}
      data={waiterOptions || []}
      value={currentWaiterId}
      onChange={handleChange}
      disabled={updateTable.isPending}
      clearable
      searchable
    />
  );
}
